import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
	ContractsListParams,
	CreateContractBody,
	UpdateContractBody,
} from '@/types/contracts'
import {
	createContract,
	deleteContract,
	getContractById,
	getContracts,
	updateContract,
} from '../services/contracts'

// ── Query keys ────────────────────────────────────────────────────────────────

export const contractKeys = {
	all: ['contracts'] as const,
	list: (params?: ContractsListParams) =>
		[...contractKeys.all, 'list', params] as const,
	detail: (id: string) => [...contractKeys.all, 'detail', id] as const,
}

// ── GET /contracts ────────────────────────────────────────────────────────────

export function useContracts(params?: ContractsListParams) {
	return useQuery({
		queryKey: contractKeys.list(params),
		queryFn: () => getContracts(params),
		staleTime: 2 * 60 * 1000, // 2 minutes
	})
}

// ── GET /contracts/:id ────────────────────────────────────────────────────────

export function useContractById(id: string) {
	return useQuery({
		queryKey: contractKeys.detail(id),
		queryFn: () => getContractById(id),
		enabled: !!id,
	})
}

// ── POST /contracts ───────────────────────────────────────────────────────────

export function useCreateContract() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (body: CreateContractBody) => createContract(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: contractKeys.all })
		},
	})
}

// ── PATCH /contracts/:id ──────────────────────────────────────────────────────

export function useUpdateContract() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: UpdateContractBody }) =>
			updateContract(id, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: contractKeys.all })
		},
	})
}

// ── DELETE /contracts/:id ─────────────────────────────────────────────────────

export function useDeleteContract() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => deleteContract(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: contractKeys.all })
		},
	})
}
